import axios from 'axios'
import config from '../Config'
import { HTML_LANG } from '../i18n'
import { KEYS, readJSON, removeKey, writeJSON } from '../Utils/storage'

/* Instapres v1 API (pres repo: api/v1.py, docs/API_V1.md). Every call
   resolves to the response body or throws; callers show their own error
   state. ?lang= is on every request because caches key on the URL only. */
const client = axios.create({ baseURL: `${config.API_URL}/v1`, timeout: 15000 })

const followedParam = (followed) =>
  followed?.length ? { followedSources: followed.join(',') } : {}

async function get(path, params = {}) {
  const response = await client.get(path, { params: { ...params, lang: HTML_LANG } })
  return response.data
}

async function post(path, body, params = {}) {
  const response = await client.post(path, body, { params: { ...params, lang: HTML_LANG } })
  return response.data
}

const enc = encodeURIComponent

/* The crossword's personal calls (/api/me/crossword/..., pres
   api/crossword.py) need an anonymous profile (POST /api/me). This browser
   makes one the first time it saves something (start, solve, a new name);
   reads without one answer null. A profile that's gone (401) is replaced.
   Never cached: unique ?_= on every call, like the old "Moje teme" calls. */
const meClient = axios.create({ baseURL: `${config.API_URL}/me`, timeout: 15000 })
let creatingProfile = null

function ensureProfile() {
  const token = readJSON(KEYS.profileToken, null)
  if (token) return Promise.resolve(token)
  if (!creatingProfile) {
    creatingProfile = axios
      .post(`${config.API_URL}/me`, {}, { timeout: 15000 })
      .then(({ data }) => {
        writeJSON(KEYS.profileToken, data.token)
        return data.token
      })
      .finally(() => {
        creatingProfile = null
      })
  }
  return creatingProfile
}

async function me(method, path, { body, params, create = false } = {}, retried = false) {
  const token = create ? await ensureProfile() : readJSON(KEYS.profileToken, null)
  if (!token) return null
  try {
    const response = await meClient.request({
      method,
      url: path,
      data: body,
      params: { ...params, _: Date.now() },
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data
  } catch (e) {
    if (e?.response?.status !== 401) throw e
    removeKey(KEYS.profileToken)
    return create && !retried ? me(method, path, { body, params, create }, true) : null
  }
}

export const hasProfile = () => Boolean(readJSON(KEYS.profileToken, null))

/* Feed page parameters. `refresh` = the reader asked for the newest (the
   pill, tap to refresh): a unique value gets past HTTP caches and makes the
   API recompute the page. */
const pageParams = ({ sort, cursor, followed, refresh }) => ({
  ...(sort ? { sort } : {}),
  ...(cursor ? { cursor } : {}),
  ...followedParam(followed),
  ...(refresh ? { refresh: Date.now() } : {}),
})

export const api = {
  // The same for everyone (no ?lang=), so one CDN-cached URL (ads spec 3.1).
  config: () => client.get('/config').then((response) => response.data),

  feed: ({ category, sort, ...page }) =>
    get('/feed', {
      ...(category ? { category } : {}),
      ...pageParams({ ...page, sort: sort === 'new' ? 'new' : null }),
    }),

  newCount: ({ category, sort, since, snapshot }) =>
    get('/feed/new-count', {
      ...(category ? { category } : {}),
      ...(sort === 'new' ? { sort } : {}),
      since,
      ...(snapshot ? { snapshot } : {}),
    }),

  story: (id, { followed } = {}) => get(`/stories/${enc(id)}`, followedParam(followed)),

  trending: ({ limit = 15, exclude, category } = {}) =>
    get('/topics/trending', {
      limit,
      ...(exclude?.length ? { exclude: exclude.join(',') } : {}),
      ...(category ? { category } : {}),
    }),

  topic: (slug) => get(`/topics/${enc(slug)}`),
  // newSince (from an "Aktuelna tema" push): stories since then come with isFresh (NOVO).
  topicStories: (slug, { newSince, ...page }) =>
    get(`/topics/${enc(slug)}/stories`, { ...pageParams(page), ...(newSince ? { newSince } : {}) }),

  sources: () => get('/sources'),
  source: (slug) => get(`/sources/${enc(slug)}`),
  sourceStories: (slug, page) => get(`/sources/${enc(slug)}/stories`, pageParams(page)),

  categories: () => get('/categories'),
  category: (slug) => get(`/categories/${enc(slug)}`),
  categoryStories: (slug, page) => get(`/categories/${enc(slug)}/stories`, pageParams(page)),

  search: (q) => get('/search', { q }),
  searchStories: (q, page) => get('/search/stories', { q, ...pageParams(page) }),

  suggest: (q) => get('/topics/suggest', { q }),

  myTopicsFeed: (topics, { cursor, followed, refresh }) =>
    post('/my-topics/feed', {
      topics,
      cursor: cursor || null,
      followedSources: followed || [],
      ...(refresh ? { refresh: true } : {}),
    }),

  // Fire and forget; sendBeacon survives the tab switching to the article.
  articleOpened: (articleId) => {
    if (!articleId) return
    const url = `${config.API_URL}/v1/articles/${articleId}/open`
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url)) return
    } catch (e) {
      // fall through
    }
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
  },

  // Daily crossword. Public: the puzzle (with its solution), the archive, the Top 10.
  crosswordToday: () => get('/crosswords/today'),
  crossword: (day) => get(`/crosswords/${enc(day)}`),
  crosswordMonth: (month) => get('/crosswords', { month }),
  crosswordTop: (day) => get(`/crosswords/${enc(day)}/top`),
  // Personal: name, streak and best time; results; the timer's start; a solve.
  crosswordMe: () => me('get', '/crossword'),
  crosswordNewName: () => me('post', '/crossword/name', { create: true }),
  crosswordResults: (month) => me('get', '/crossword/results', { params: { month } }),
  crosswordResult: (day) => me('get', `/crossword/${enc(day)}`),
  crosswordStart: (day) => me('post', `/crossword/${enc(day)}/start`, { create: true }),
  crosswordSolve: (day, { grid, seconds, revealed }) =>
    me('post', `/crossword/${enc(day)}/solve`, { body: { grid, seconds, revealed }, create: true }),

  // One-time move of the old server-side "Moje teme" profile to this browser.
  legacyMyTopics: async (token) => {
    const response = await axios.get(`${config.API_URL}/me/topics`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { _: Date.now() },
      timeout: 15000,
    })
    return response.data
  },
}
